import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle } from "lucide-react";

/**
 * CinematicNav — barra fissa minimal (ARIA · ECOSISTEMA · PRODOTTI) +
 * "Parla con Aria" primaria + "Apri Workspace"/"Accedi" condizionali.
 * Funziona da qualsiasi pagina: se l'utente clicca "Ecosistema" e non è
 * sul Hub, navega prima a "/" e poi scrolla all'atto target.
 */
export default function CinematicNav() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Smart navigation: anchor su Hub se siamo lì, altrimenti navigate("/") + scroll dopo mount
    const goToAct = (actTestId) => {
        setMobileOpen(false);
        const onHub = location.pathname === "/";
        if (onHub) {
            const el = document.querySelector(`[data-testid="${actTestId}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
            navigate("/", { state: { scrollTo: actTestId } });
            // Dopo navigate, attendi mount poi scrolla
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="${actTestId}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 600);
        }
    };

    const openAria = () => {
        setMobileOpen(false);
        try { window.dispatchEvent(new Event("open-aria")); } catch { /* noop */ }
    };

    const ITEMS = [
        { label: "Aria", to: "/aria" },
        { label: "Ecosistema", action: () => goToAct("act-07") },
        { label: "Prodotti", to: "/prodotti" },
    ];

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 transition-all"
            style={{
                background: scrolled ? "rgba(5,5,15,0.72)" : "linear-gradient(180deg, rgba(5,5,15,0.55), rgba(5,5,15,0.0))",
                backdropFilter: "blur(16px) saturate(140%)",
                WebkitBackdropFilter: "blur(16px) saturate(140%)",
                borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
            }}
            data-testid="cinematic-nav"
        >
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 md:py-3.5 flex items-center justify-between gap-3">
                {/* Brand */}
                <Link
                    to="/"
                    onClick={() => {
                        // Se siamo già sulla home, il Link non rifa il render → scroll manuale.
                        if (typeof window !== "undefined" && window.location.pathname === "/") {
                            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                        }
                    }}
                    className="flex items-center text-white/95 flex-none" data-testid="nav-brand" aria-label="TotalKlick home">
                    <img src="/brand/totalklick-logo.svg" alt="TotalKlick" className="h-5 md:h-6 w-auto" style={{ filter: "drop-shadow(0 0 18px rgba(79,139,255,0.45))" }} />
                </Link>

                {/* Menu desktop */}
                <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
                    {ITEMS.map((it) => (
                        it.to ? (
                            <Link
                                key={it.label}
                                to={it.to}
                                className="px-3 py-1.5 text-[12.5px] text-white/65 hover:text-white tracking-[0.16em] uppercase transition"
                                style={{ fontFamily: "JetBrains Mono, monospace" }}
                                data-testid={`nav-${it.label.toLowerCase()}`}
                            >
                                {it.label}
                            </Link>
                        ) : (
                            <button
                                key={it.label}
                                type="button"
                                onClick={it.action}
                                className="px-3 py-1.5 text-[12.5px] text-white/65 hover:text-white tracking-[0.16em] uppercase transition"
                                style={{ fontFamily: "JetBrains Mono, monospace" }}
                                data-testid={`nav-${it.label.toLowerCase()}`}
                            >
                                {it.label}
                            </button>
                        )
                    ))}
                </nav>

                {/* Right side */}
                <div className="flex items-center gap-2 flex-none">
                    <button
                        type="button"
                        onClick={openAria}
                        className="inline-flex items-center gap-1.5 px-3 md:px-3.5 py-1.5 rounded-full text-[11.5px] md:text-[12px] text-white transition hover:scale-[1.03]"
                        style={{
                            background: "linear-gradient(135deg, #4F8BFF 0%, #2B5AFF 100%)",
                            fontFamily: "Geist, sans-serif", fontWeight: 500,
                            boxShadow: "0 12px 30px -8px rgba(79,139,255,0.5)",
                        }}
                        data-testid="nav-talk-with-aria"
                    >
                        <MessageCircle size={11} />
                        <span className="hidden sm:inline">Parla con Aria</span>
                        <span className="sm:hidden">Aria</span>
                    </button>
                    {user ? (
                        <Link to="/app" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] text-white/85 hover:text-white transition" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.14em", textTransform: "uppercase" }} data-testid="nav-open-workspace">
                            Workspace
                        </Link>
                    ) : (
                        <Link to="/login" className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] text-white/65 hover:text-white transition" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.14em", textTransform: "uppercase" }} data-testid="nav-login">
                            Accedi
                        </Link>
                    )}

                    {/* Burger mobile */}
                    <button
                        type="button"
                        className="md:hidden w-9 h-9 rounded-full grid place-items-center text-white/85"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="nav-burger"
                        aria-label="Menu"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {mobileOpen ? <>
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </> : <>
                                <line x1="3" y1="7" x2="21" y2="7" />
                                <line x1="3" y1="17" x2="21" y2="17" />
                            </>}
                        </svg>
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="md:hidden px-4 pb-4" data-testid="nav-mobile-drawer">
                    <div
                        className="rounded-2xl py-2"
                        style={{
                            background: "rgba(5,5,15,0.98)",
                            border: "1px solid rgba(79,139,255,0.18)",
                            backdropFilter: "blur(24px)",
                            WebkitBackdropFilter: "blur(24px)",
                            boxShadow: "0 24px 60px -16px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
                        }}
                    >
                        {ITEMS.map((it) => (
                            it.to ? (
                                <Link key={it.label} to={it.to} onClick={() => setMobileOpen(false)} className="block px-5 py-3 text-[13px] text-white/85 hover:text-white tracking-[0.16em] uppercase transition" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    {it.label}
                                </Link>
                            ) : (
                                <button key={it.label} type="button" onClick={it.action} className="block w-full text-left px-5 py-3 text-[13px] text-white/85 hover:text-white tracking-[0.16em] uppercase transition" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    {it.label}
                                </button>
                            )
                        ))}
                        <div className="border-t border-white/5 mt-1 pt-1">
                            {user ? (
                                <Link to="/app" onClick={() => setMobileOpen(false)} className="block px-5 py-3 text-[13px] text-[#9EC3FF] hover:text-white tracking-[0.16em] uppercase transition" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    Apri Workspace →
                                </Link>
                            ) : (
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-5 py-3 text-[13px] text-white/65 hover:text-white tracking-[0.16em] uppercase transition" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                    Accedi
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
